import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, MessageCircle, ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { productsApi } from "@/lib/api";
import { waLink } from "@/lib/brand";
import Reveal from "./Reveal";
import { SectionTitle } from "./Categories";

const PLACEHOLDERS = [
  { name: "Bolo Chocolate com Brigadeiros", category: "bolos", image: "https://images.unsplash.com/photo-1621868402792-a5c9fa6866a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", _placeholder: true },
  { name: "Caixa de Brigadeiros Gourmet", category: "doces", image: "https://images.pexels.com/photos/37306625/pexels-photo-37306625.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=700", _placeholder: true },
  { name: "Mesa de Doces Personalizada", category: "encomendas", image: "https://images.unsplash.com/photo-1583338917451-face2751d8d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", _placeholder: true },
  { name: "Cupcake Especial", category: "doces", image: "https://images.unsplash.com/photo-1549590143-d5855148a9d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", _placeholder: true },
];

const ProductCard = ({ p, onOpen, delay }) => (
  <Reveal delay={delay}>
    <motion.div whileHover={{ y: -6 }} className="group bg-card rounded-2xl overflow-hidden border border-beige hover:shadow-xl transition-shadow duration-300 h-full flex flex-col" data-testid="product-card">
      <div className="relative aspect-[4/5] overflow-hidden bg-muted">
        {p.image ? (
          <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon size={40} /></div>
        )}
        <span className="absolute top-3 left-3 bg-cream/90 backdrop-blur text-[10px] uppercase tracking-widest text-choco px-3 py-1 rounded-full">{p.category}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif text-2xl text-choco leading-tight">{p.name}</h3>
        {p.price ? <p className="text-gold font-semibold mt-1">{p.price}</p> : <p className="text-xs text-muted-foreground mt-1">Sob consulta</p>}
        <button onClick={() => onOpen(p)} data-testid="product-details-btn"
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-choco text-choco px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-choco hover:text-cream transition-colors duration-300">
          <Eye size={15} /> Ver detalhes
        </button>
      </div>
    </motion.div>
  </Reveal>
);

const Highlights = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
  productsApi.list()
    .then((data) => {
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.products)
          ? data.products
          : Array.isArray(data?.data)
            ? data.data
            : [];

      setProducts(list);
    })
    .catch(() => setProducts([]));
}, []);

  const items = Array.isArray(products) && products.length > 0
  ? products
  : PLACEHOLDERS;

  return (
    <section id="destaques" className="bg-[#F0EBE1] py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-5">
        <Reveal><SectionTitle overline="Seleção especial" title="Nossos Destaques" /></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.slice(0, 8).map((p, i) => (
            <ProductCard key={p.id || i} p={p} delay={(i % 4) * 0.1} onOpen={setSelected} />
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg bg-cream border-beige">
          {selected && (
            <>
              <div className="aspect-video rounded-xl overflow-hidden bg-muted mb-2">
                {selected.image ? <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon size={40} /></div>}
              </div>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-choco">{selected.name}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground leading-relaxed">{selected.description || "Produto artesanal feito com carinho pela Deco Sweet. Entre em contato para mais detalhes e sabores disponíveis."}</p>
              {selected.price && <p className="text-gold font-semibold text-lg">{selected.price}</p>}
              <a href={waLink(`Olá! Gostaria de encomendar: ${selected.name}.`)} data-testid="dialog-whatsapp"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream px-6 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-gold transition-colors">
                <MessageCircle size={17} /> Encomendar pelo WhatsApp
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Highlights;
